import React from 'react';
import clsx from 'clsx';
import {
  createCalendar,
  DateValue,
  toCalendarDate,
} from '@internationalized/date';
import { AriaCalendarProps, useCalendar } from '@react-aria/calendar';
import { useDateFormatter, useLocale } from '@react-aria/i18n';
import { filterDOMProps, mergeProps } from '@react-aria/utils';
import styles from '@danske/sapphire-css/components/calendar/calendar.module.css';

import {
  CalendarState,
  useCalendarState,
  type CalendarStateOptions,
} from './useCalendarState';
import { Button } from '../../Button';
import { CalendarHeader } from './CalendarHeader';
import { CalendarDaysGrid } from './CalendarDaysGrid';
import { CalendarMonthsGrid } from './CalendarMonthsGrid';
import { CalendarYearsGrid } from './CalendarYearsGrid';
import { sentenceCase } from './utils';
import {
  SapphireStyleProps,
  useSapphireStyleProps,
  useThemeCheck,
} from '../../utils';
import { BaseComponentKeys, GlobalDomAttributes } from '../../utils/types';

export interface PredefinedDate {
  label: string;
  date: DateValue;
}

export interface CalendarProps<
  T extends DateValue = DateValue,
  P extends string = string
> extends SapphireStyleProps,
    GlobalDomAttributes,
    Pick<
      AriaCalendarProps<T>,
      | 'minValue'
      | 'maxValue'
      | 'isDateUnavailable'
      | 'isReadOnly'
      | 'autoFocus'
      | 'focusedValue'
      | 'onFocusChange'
      | 'isDisabled'
      | BaseComponentKeys
    >,
    Omit<CalendarStateOptions<T, P>, 'createCalendar'> {
  /**
   * This callback also gives you the information if the selected date was a
   * predefined date according to the `predefinedDates` prop.
   */
  onChange?: CalendarStateOptions<DateValue, P>['onChange'];
  /**
   * Whether week numbers are visible as a first column
   */
  showWeekNumbers?: boolean;
  /**
   * A set of predefined dates the user can more conveniently select.
   */
  predefinedDates?: Record<P, PredefinedDate>;
  /**
   * If the calendar should always show a fixed number of rows regardless of
   * the current month's number of weeks. This helps preserve the calendar
   * height as the user scrolls through months.
   * @default false
   */
  showFixedWeeks?: boolean;
  /**
   * The calendar size.
   * @default 'lg'
   */
  size?: 'md' | 'lg';
}

/**
 * Displays a calendar and allows users to select a single date. It displays a grid of days in one
 * month, by default, and the user can navigate to previous and next months. It's also possible
 * to navigate to months and years view, to quickly jump to a date far away from the focused date.
 */
export const Calendar = <
  T extends DateValue = DateValue,
  P extends string = string
>({
  showWeekNumbers,
  predefinedDates,
  showFixedWeeks = false,
  size = 'lg',
  ...props
}: Omit<CalendarProps<T, P>, 'locale'>): JSX.Element => {
  useThemeCheck();
  const { locale } = useLocale();

  const { styleProps } = useSapphireStyleProps(props);
  const state = useCalendarState({
    selectionAlignment: 'start',
    ...props,
    locale,
    createCalendar,
  });

  const monthDateFormatter = useDateFormatter({
    month: 'long',
    year: 'numeric',
    era:
      state.visibleRange.start.calendar.identifier === 'gregory' &&
      state.visibleRange.start.era === 'BC'
        ? 'short'
        : undefined,
    calendar: state.visibleRange.start.calendar.identifier,
    timeZone: state.timeZone,
  });

  const { calendarProps, prevButtonProps, nextButtonProps } = useCalendar(
    props,
    state
  );

  const renderGrid = () => {
    switch (state.view) {
      case 'years':
        return <CalendarYearsGrid state={state as CalendarState<string>} />;
      case 'months':
        return <CalendarMonthsGrid state={state as CalendarState<string>} />;
      case 'days':
        return (
          <CalendarDaysGrid
            key="days"
            state={state}
            showWeekNumbers={showWeekNumbers}
            showFixedWeeks={showFixedWeeks}
          />
        );
      default:
        return null;
    }
  };

  const renderTitle = () => {
    switch (state.view) {
      case 'days':
        return sentenceCase(
          monthDateFormatter.format(
            state.visibleRange.start.toDate(state.timeZone)
          )
        );
      case 'months':
        return state.focusedDate.year;
      case 'years':
        return `${state.visibleRange.start.year} — ${
          state.visibleRange.end.year - 1
        }`;
    }
  };

  return (
    <div
      className={styles['sapphire-calendar']}
      {...mergeProps(calendarProps, filterDOMProps(props, { global: true }))}
    >
      <div
        className={clsx(
          styles['sapphire-calendar__table-container'],
          styleProps.className,
          {
            [styles['sapphire-calendar__table-container--md']]: size === 'md',
          }
        )}
        style={styleProps.style}
      >
        <CalendarHeader
          prevButtonProps={prevButtonProps}
          nextButtonProps={nextButtonProps}
          size={size}
        >
          {state.canBroadenView ? (
            <Button
              onPress={state.broadenView}
              isDisabled={state.isDisabled}
              variant="tertiary"
              size={size}
            >
              {renderTitle()}
            </Button>
          ) : (
            renderTitle()
          )}
        </CalendarHeader>
        {renderGrid()}
      </div>
      {predefinedDates && (
        <div className={styles['sapphire-calendar__custom-ranges']}>
          {(Object.entries(predefinedDates) as Array<[P, PredefinedDate]>).map(
            ([key, { label, date }]) => (
              <div key={key}>
                <Button
                  variant="text"
                  onPress={() => {
                    state.setValue(toCalendarDate(date), key);
                    state.setFocusedDate(toCalendarDate(date));
                  }}
                >
                  {label}
                </Button>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};
